import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminMarketplaceIntegrations.css';

function AdminMarketplaceIntegrations() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [formData, setFormData] = useState({
    marketplace: '',
    name: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    refreshToken: '',
    config: {},
    isActive: false,
  });

  const { data: integrations, isLoading } = useQuery({
    queryKey: ['admin-marketplace-integrations'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/marketplace-integrations');
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post('/admin/marketplace-integrations', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-marketplace-integrations']);
      setEditingIntegration(null);
      setShowModal(false);
      resetForm();
      notification.success('Marketplace integracija išsaugota');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Nepavyko išsaugoti integracijos');
    },
  });

  const resetForm = () => {
    setFormData({
      marketplace: '',
      name: '',
      apiKey: '',
      apiSecret: '',
      accessToken: '',
      refreshToken: '',
      config: {},
      isActive: false,
    });
  };

  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    setShowModal(true);
    setFormData({
      marketplace: integration.marketplace,
      name: integration.name,
      apiKey: integration.api_key === '***' ? '' : integration.api_key || '',
      apiSecret: integration.api_secret === '***' ? '' : integration.api_secret || '',
      accessToken: integration.access_token === '***' ? '' : integration.access_token || '',
      refreshToken: integration.refresh_token === '***' ? '' : integration.refresh_token || '',
      config: integration.config || {},
      isActive: integration.is_active,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  if (isLoading) return <div className="admin-marketplace-page container">Kraunama...</div>;

  return (
    <div className="admin-marketplace-page container">
      <div className="marketplace-header">
        <h1>Marketplace'ų integracijos</h1>
      </div>

      <div className="marketplace-grid">
        {['amazon', 'ebay', 'etsy'].map((marketplace) => {
          const integration = integrations?.find((i) => i.marketplace === marketplace);
          return (
            <div key={marketplace} className="marketplace-card">
              <h3 className="capitalize">{marketplace}</h3>
              {integration ? (
                <div className="integration-info">
                  <p><strong>Pavadinimas:</strong> {integration.name}</p>
                  <p>
                    <strong>Statusas:</strong>{' '}
                    <span className={`status-badge ${integration.is_active ? 'active' : 'inactive'}`}>
                      {integration.is_active ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </p>
                  <button className="btn btn-secondary" onClick={() => handleEdit(integration)}>
                    Redaguoti
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setFormData({ 
                      marketplace: marketplace,
                      name: '',
                      apiKey: '',
                      apiSecret: '',
                      accessToken: '',
                      refreshToken: '',
                      config: {},
                      isActive: false,
                    });
                    setEditingIntegration(null);
                    setShowModal(true);
                  }}
                >
                  Sukurti integraciją
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingIntegration(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingIntegration ? 'Redaguoti integraciją' : 'Nauja integracija'}</h2>
            <form onSubmit={handleSubmit} className="integration-form">
              <div className="form-group">
                <label>Marketplace *</label>
                <select
                  value={formData.marketplace}
                  onChange={(e) => setFormData({ ...formData, marketplace: e.target.value })}
                  required
                  disabled={!!editingIntegration}
                >
                  <option value="amazon">Amazon</option>
                  <option value="ebay">eBay</option>
                  <option value="etsy">Etsy</option>
                </select>
              </div>

              <div className="form-group">
                <label>Pavadinimas *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>API Key</label>
                <input
                  type={showApiKey.apiKey ? 'text' : 'password'}
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="Įveskite API key"
                />
                <button
                  type="button"
                  className="btn-toggle-visibility"
                  onClick={() => setShowApiKey({ ...showApiKey, apiKey: !showApiKey.apiKey })}
                >
                  {showApiKey.apiKey ? 'Slėpti' : 'Rodyti'}
                </button>
              </div>

              <div className="form-group">
                <label>API Secret</label>
                <input
                  type={showApiKey.apiSecret ? 'text' : 'password'}
                  value={formData.apiSecret}
                  onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                  placeholder="Įveskite API secret"
                />
                <button
                  type="button"
                  className="btn-toggle-visibility"
                  onClick={() => setShowApiKey({ ...showApiKey, apiSecret: !showApiKey.apiSecret })}
                >
                  {showApiKey.apiSecret ? 'Slėpti' : 'Rodyti'}
                </button>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Aktyvus
                </label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  Išsaugoti
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingIntegration(null);
                    resetForm();
                  }}
                >
                  Atšaukti
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMarketplaceIntegrations;
